import { useQuery, useApolloClient } from '@apollo/client';
import { GET_PLAYBOOKS, GetPlaybooksQuery } from '@zaplify/graphql';
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSdk } from '../../../sdk';

type PlaybooksViewContextType = {
    searchInput: string;
    setSearchInput: (value: string) => void;
    playbooks: GetPlaybooksQuery['Groups'];
    deletePlaybook: (id: string) => void;
};

const PlaybooksViewContext = createContext<PlaybooksViewContextType | undefined>(undefined);

export function PlaybooksViewProvider({ children }: { children: React.ReactNode }) {
    'use no memo';
    const [searchInput, setSearchInput] = useState('');
    const client = useApolloClient();
    const {
        playbook: { deleteGroup: deleteGroupMutation },
    } = useSdk();

    const { mutateAsync: deleteGroup } = useMutation(deleteGroupMutation());

    const { data: playbooksData, previousData } = useQuery(GET_PLAYBOOKS, {
        variables: {
            name: '%' + searchInput + '%',
        },
    });

    const playbooks = useMemo(() => playbooksData?.Groups || previousData?.Groups || [], [playbooksData, previousData]);

    const handleDeletePlaybook = useCallback(
        async (id: string) => {
            await deleteGroup({ groupId: id });
            await client.refetchQueries({
                updateCache: (cache) => cache.evict({ fieldName: 'Groups' }),
            });
        },
        [deleteGroup, client]
    );

    return (
        <PlaybooksViewContext.Provider
            value={{
                playbooks,
                searchInput,
                setSearchInput,
                deletePlaybook: handleDeletePlaybook,
            }}
        >
            {children}
        </PlaybooksViewContext.Provider>
    );
}

export function usePlaybooksView() {
    const context = useContext(PlaybooksViewContext);
    if (context === undefined) {
        throw new Error('usePlaybooksView must be used within a PlaybooksViewProvider');
    }
    return context;
}
